import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Ear, HeartHandshake, ShieldCheck } from "lucide-react";
import { KineticLine, FadeUp, Overline } from "../components/motion";
import { IMAGES } from "../constants/site";

const chapters = [
  {
    num: "01",
    icon: BookOpen,
    title: "Every Student, Full Potential",
    text: "Our schools do much more than provide academic knowledge. They prepare young people to become confident, responsible, compassionate, and engaged members of society. I will champion the opportunity, support, and encouragement every student needs — regardless of background, culture, abilities, or circumstances.",
    span: "md:col-span-7",
  },
  {
    num: "02",
    icon: ShieldCheck,
    title: "Support Our Educators",
    text: "Great schools are built by great educators. I will advocate for the resources, respect, and working conditions teachers and staff need to help every learner thrive.",
    span: "md:col-span-5",
  },
  {
    num: "03",
    icon: Ear,
    title: "Listen to Families",
    text: "Families know their children best. I am committed to genuine, ongoing listening — understanding different perspectives and working collaboratively to find meaningful, practical solutions.",
    span: "md:col-span-5",
  },
  {
    num: "04",
    icon: HeartHandshake,
    title: "Strengthen School–Community Partnerships",
    text: "Schools are strongest when communities stand behind them. I will work to deepen the partnership between our schools and the diverse communities of Vaughan, so every child feels valued, respected, included, supported, and empowered to succeed.",
    span: "md:col-span-7",
  },
];

export default function Platform() {
  return (
    <main data-testid="platform-page" className="pt-32 lg:pt-40">
      <section className="mx-auto max-w-7xl px-6 lg:px-10">
        <Overline>The Platform</Overline>
        <h1 className="mt-6 font-display text-5xl font-bold leading-[0.95] tracking-tighter text-ink sm:text-6xl lg:text-7xl">
          <KineticLine delay={0.1}>A manifesto for</KineticLine>
          <KineticLine delay={0.22}>
            <span className="text-terracotta">our schools.</span>
          </KineticLine>
        </h1>
        <FadeUp delay={0.3}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-stone2 md:text-lg">
            Four commitments, one goal: a school system where every child in Vaughan can reach
            their full potential.
          </p>
        </FadeUp>
      </section>

      <section data-testid="platform-chapters" className="mx-auto mt-20 max-w-7xl px-6 lg:px-10">
        <div className="grid gap-6 md:grid-cols-12">
          {chapters.map((c, i) => (
            <FadeUp key={c.num} delay={i * 0.08} className={c.span}>
              <article
                data-testid={`platform-chapter-${c.num}`}
                className="group relative h-full overflow-hidden border border-sand bg-white p-10 transition-colors duration-300 hover:border-terracotta/50 lg:p-12"
              >
                <span
                  className="pointer-events-none absolute -bottom-8 -right-2 font-display text-[10rem] font-bold leading-none text-stone-200/60 transition-colors duration-500 group-hover:text-terracotta/15"
                  aria-hidden="true"
                >
                  {c.num}
                </span>
                <c.icon size={30} className="text-terracotta" strokeWidth={1.5} />
                <h2 className="mt-8 font-display text-2xl font-bold tracking-tight text-ink md:text-3xl">
                  Chapter {c.num} — {c.title}
                </h2>
                <p className="relative mt-5 max-w-xl text-sm leading-relaxed text-stone2 md:text-base">
                  {c.text}
                </p>
              </article>
            </FadeUp>
          ))}
        </div>
      </section>

      <section data-testid="platform-image-band" className="mx-auto mt-24 max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <div className="relative overflow-hidden border border-sand">
            <img
              src={IMAGES.classroom}
              alt="Students learning together in a classroom"
              data-testid="platform-image-classroom"
              className="h-80 w-full object-cover md:h-96"
            />
            <div className="absolute inset-0 bg-navy/30" aria-hidden="true" />
            <p className="absolute bottom-8 left-8 max-w-lg font-serif text-2xl italic leading-snug text-cream md:text-3xl">
              "Education is fundamental to the future growth and success of Vaughan."
            </p>
          </div>
        </FadeUp>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
        <FadeUp>
          <div className="flex flex-col items-start justify-between gap-8 border border-sand bg-white p-10 md:flex-row md:items-center lg:p-14">
            <h2 className="max-w-xl font-display text-3xl font-bold leading-tight tracking-tight text-ink md:text-4xl">
              Stand with Winnie for stronger schools.
            </h2>
            <Link
              to="/get-involved"
              data-testid="platform-cta-volunteer"
              className="group inline-flex items-center gap-2 rounded-sm bg-terracotta px-7 py-3.5 font-semibold text-cream transition-all duration-300 hover:bg-terracotta-dark active:scale-[0.98]"
            >
              Get Involved
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </FadeUp>
      </section>
    </main>
  );
}
